export const ADD_PRODUCT_STORAGE_KEY = "appState_addProduct";

export const ERP_UPLOAD_URL = "https://erp.thriftops.com/Upload/appImages.php";

/** Same shape as `appState_addProduct` in localStorage. */
export const buildAddProductAppStateSnapshot = ({
  currentStep,
  formData,
  processedImages,
}) => {
  const { imageProcessing, ...persistableFormData } = formData;
  return {
    currentStep,
    processedImages: filterPersistableImageUrls(processedImages),
    formData: {
      ...persistableFormData,
      uploadedImages: filterPersistableImageUrls(
        persistableFormData.uploadedImages || [],
      ),
    },
  };
};

export const formatSeoTags = (formData) => {
  if (formData?.seo_tags) return formData.seo_tags;
  const tags = formData?.tags;
  if (Array.isArray(tags)) return tags.join(", ");
  return tags || "";
};

export const buildCreateProductPayload = ({
  companyId,
  formData,
  images,
  uploadedBy,
  status,
  rawResponse,
}) => ({
  company_id: companyId,
  title: formData.title || "Product",
  sku: formData.sku || `SKU-${Date.now()}`,
  description: formData.description || "",
  seo_tags: formatSeoTags(formData),
  price: Number(formData.price || 0),
  quantity: Number(formData.quantity || 1),
  images,
  uploaded_by: uploadedBy,
  status,
  raw_response: rawResponse,
});

export const isHostedImageUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith("http://") || url.startsWith("https://")) &&
  !url.startsWith("blob:");

/** Drop blob/data URLs that cannot survive a page refresh. */
export const filterPersistableImageUrls = (urls) => {
  if (!Array.isArray(urls)) return [];
  return urls.filter((url) => isHostedImageUrl(url));
};

export const parseErpUploadResponse = (data) => {
  if (data?.status === "error") return []; // error case
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.images)) return data.images.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (data?.url) return [data.url];
  return [];
};

export const uploadFilesToERP = async (files, fieldName = "images[]") => {
  if (!files?.length) return [];

  const formData = new FormData();
  const uniqueId = Date.now() + Math.random().toString(36).slice(2);

  files.forEach((file, i) => {
    const uploadFile =
      file instanceof File
        ? file
        : new File([file], `image-${uniqueId}-${i + 1}.png`, {
            type: file.type || "image/png",
          });
    formData.append("images[]", uploadFile, uploadFile.name);
  });

  // Random delay add karo race condition avoid karne ke liye
  await new Promise((r) => setTimeout(r, Math.random() * 500));

  const uploadRes = await fetch(ERP_UPLOAD_URL, {
    method: "POST",
    body: formData,
  });

  if (!uploadRes.ok) {
    throw new Error(`ERP upload failed: ${uploadRes.status}`);
  }

  const data = await uploadRes.json();
  console.log("🔍 ERP raw response:", JSON.stringify(data)); // ADD KAR
  const urls = parseErpUploadResponse(data);

  return urls;
};

export const urlToFile = async (url, filename = "image.png") => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to read image: ${res.status}`);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
};

/**
 * Convert Files, Blobs, blob:/data: URLs, or existing hosted URLs
 * into hosted ERP URLs while preserving input order.
 */
export const ensureHostedImageUrls = async (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const slots = new Array(items.length).fill(null);
  const filesToUpload = [];
  const fileSlotIndexes = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;

    if (typeof item === "string" && isHostedImageUrl(item)) {
      slots[i] = item;
      continue;
    }

    if (item instanceof File) {
      filesToUpload.push(item);
      fileSlotIndexes.push(i);
      continue;
    }

    if (item instanceof Blob) {
      filesToUpload.push(
        new File([item], `image-${i + 1}.png`, {
          type: item.type || "image/png",
        }),
      );
      fileSlotIndexes.push(i);
      continue;
    }

    if (
      typeof item === "string" &&
      (item.startsWith("blob:") || item.startsWith("data:"))
    ) {
      const file = await urlToFile(item, `image-${i + 1}.png`);
      filesToUpload.push(file);
      fileSlotIndexes.push(i);
    }
  }

  if (filesToUpload.length > 0) {
    const uploadedUrls = await uploadFilesToERP(filesToUpload);
    fileSlotIndexes.forEach((slotIndex, uploadIndex) => {
      slots[slotIndex] = uploadedUrls[uploadIndex];
    });
  }

  return slots.filter(Boolean);
};

export const handleEbayIntegrate = async (e) => {
  e.stopPropagation();
  try {
    const res = await fetch(
      "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/ebayUrlFB",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const data = await res.json();
    if (data?.url) {
      window.location.href = data.url;
    } else {
      console.error("eBay auth URL not received");
    }
  } catch (err) {
    console.error("eBay integrate error:", err);
  }
};

export const buildEbayAspects = (categoryAspects = []) => {
  const aspects = {};
  categoryAspects.forEach((a) => {
    aspects[a.localizedAspectName] =
      a.value && a.value.trim() !== "" ? [a.value] : ["N/A"];
  });
  return aspects;
};

export const buildMetafieldsFromAnalysis = (activeMetafields, responseData) => {
  const result = {};

  const analysis = responseData?.analysis || {};
  const aiMetafields = analysis.metafields || {};
  const productMeta = responseData?.product_metadata || {};

  activeMetafields.forEach((mf) => {
    const key = mf.key;

    if (aiMetafields.hasOwnProperty(key)) {
      result[key] = aiMetafields[key] || "";
    } else if (analysis.hasOwnProperty(key)) {
      result[key] = analysis[key] || "";
    } else if (productMeta.hasOwnProperty(key)) {
      result[key] = productMeta[key] || "";
    } else {
      result[key] = "";
    }
    console.log("result[key]", result[key]);
  });

  return result;
};

export const getVintedCategoryIcon = (categoryName) => {
  const lower = categoryName.toLowerCase();
  if (lower.includes("woman") || lower.includes("women")) return "👩";
  if (lower.includes("man") || lower.includes("men")) return "👨";
  if (lower.includes("kid") || lower.includes("child")) return "👶";
  if (lower.includes("home")) return "🏠";
  if (lower.includes("electronic")) return "📱";
  if (lower.includes("sport")) return "⚽";
  if (
    lower.includes("cloth") ||
    lower.includes("dress") ||
    lower.includes("shirt")
  )
    return "👕";
  if (lower.includes("shoe")) return "👟";
  if (lower.includes("bag")) return "👜";
  if (lower.includes("accessory")) return "⌚";
  if (lower.includes("toy")) return "🧸";
  if (lower.includes("book")) return "📚";
  if (lower.includes("beauty") || lower.includes("makeup")) return "💄";
  return "📁";
};
